import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Fade,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit,
  AccountCircle,
  Image,
  Visibility,
  Save,
  ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import Swal from 'sweetalert2';
import Header from '../Components/Header.jsx';
import StudentSidebar from './StudentSdebar.jsx';
import ProfilePhoto from '../Components/EditProfileSection/ProfilePhoto.jsx';
import CoverPhoto from '../Components/EditProfileSection/CoverPhoto.jsx';
import { useAuth } from '../../../utils/AuthContext';

const API_URL = 'https://tdtlworld.com/dss-kaas-backend/lms/user/';

const EditProfile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    visibility: 'Public',
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('edit');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const token = localStorage.getItem('jwttoken');

  const { userData, refreshUserData } = useAuth();

  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        nickname: userData.nickname || '',
        visibility: userData.visibility || 'Public',
      });
      setLoading(false);
    }
  }, [userData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVisibilityChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await axios.patch(
        API_URL,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          nickname: formData.nickname,
          visibility: formData.visibility,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refreshUserData(); // Refresh global state
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Profile updated successfully!',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to update profile. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const menuItems = [
    { id: 'edit', label: 'Edit Details', icon: <Edit /> },
    { id: 'profile-photo', label: 'Profile Photo', icon: <AccountCircle /> },
    { id: 'cover-photo', label: 'Cover Photo', icon: <Image /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Box>
                <Typography variant="h3" fontWeight={800}>Settings</Typography>
                <Typography variant="body1" color="text.secondary">Customize your profile preferences and identity.</Typography>
              </Box>
              <Button 
                variant="outlined" 
                startIcon={<Visibility />}
                component={Link}
                to="/db-shiksha/kaas/student/profile"
                sx={{ borderRadius: '50px', fontWeight: 700, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
              >
                View Profile
              </Button>
            </Box>

            <Grid container spacing={4}>
              {/* Navigation Sidebar */}
              <Grid item xs={12} md={3}>
                <Paper sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                  <List disablePadding>
                    {menuItems.map((item) => (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          selected={activeSection === item.id}
                          onClick={() => setActiveSection(item.id)}
                          sx={{
                            py: 2,
                            px: 3,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
                              '& .MuiListItemIcon-root': { color: 'primary.main' }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700 }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Content Area */}
              <Grid item xs={12} md={9}>
                <Fade in={true} timeout={500}>
                  <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: '24px', border: '1px solid', borderColor: 'divider' }}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#D4AF37' }} />
                      </Box>
                    ) : (
                      <>
                        {activeSection === 'edit' && (
                          <Box component="form" onSubmit={handleSubmit}>
                            <Typography variant="h5" fontWeight={800} sx={{ mb: 4 }}>General Information</Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="First Name"
                                  name="firstName"
                                  value={formData.firstName}
                                  onChange={handleChange}
                                  required
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Public</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Last Name"
                                  name="lastName"
                                  value={formData.lastName}
                                  onChange={handleChange}
                                  required
                                />
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Visibility</InputLabel>
                                    <Select
                                      value={formData.visibility}
                                      label="Visibility"
                                      onChange={(e) => handleVisibilityChange('visibility', e.target.value)}
                                    >
                                      <MenuItem value="Public">Public</MenuItem>
                                      <MenuItem value="All Members">All Members</MenuItem>
                                      <MenuItem value="My Connections">My Connections</MenuItem>
                                      <MenuItem value="Only Me">Only Me</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Nickname"
                                  name="nickname"
                                  value={formData.nickname}
                                  onChange={handleChange}
                                  required
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Public</Typography>
                              </Grid>
                              <Grid item xs={12} sx={{ mt: 2 }}>
                                <Button
                                  variant="contained"
                                  size="large"
                                  type="submit"
                                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                  disabled={isSaving}
                                  sx={{ px: 5, borderRadius: '50px', fontWeight: 800 }}
                                >
                                  {isSaving ? 'Saving...' : 'Save Changes'}
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {activeSection === 'profile-photo' && <ProfilePhoto />}
                        {activeSection === 'cover-photo' && <CoverPhoto />}
                      </>
                    )}
                  </Paper>
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default EditProfile;
